import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Users, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { getFamilySummary, getMySubscription } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useModule } from '../../contexts/ModuleContext';
import { AggregateSummaryCard } from './components/AggregateSummaryCard';

// Riepilogo aggregato in sola lettura, ristretto alla famiglia dell'utente (controllo lato server)
export default function ArbicsxFamilySummary() {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const { impersonatedUser, isImpersonating, updateImpersonatedUser, isAdmin } = useModule();
  const impersonateId = isImpersonating ? impersonatedUser?.id : null;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [telegramLinked, setTelegramLinked] = useState(true); // Default true per evitare il flash
  const [freshUserId, setFreshUserId] = useState(null);
  const canShowTelegramCard = !!user?.family_view && !isImpersonating && !isAdmin;

  const load = async () => {
    setLoading(true);
    try {
      const res = await getFamilySummary(impersonateId);
      setSummary(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(err.response.data?.detail || 'Riepilogo famiglia non disponibile');
        // flag spento: allineo lo stato locale (evita loop di redirect) e torno alla dashboard
        if (isImpersonating) updateImpersonatedUser?.({ family_view: false });
        else await refreshUser?.();
        navigate('/arbicsx/dashboard', { replace: true });
        return;
      }
      toast.error('Errore nel caricamento del riepilogo famiglia');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [impersonateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stato collegamento Telegram: solo al montaggio e quando la pagina torna visibile/in focus
  useEffect(() => {
    if (!canShowTelegramCard) return;
    let active = true;
    const checkTelegramStatus = async () => {
      try {
        const response = await getMySubscription();
        if (!active) return;
        setTelegramLinked(!!response.data.telegram_chat_id);
        setFreshUserId(response.data.user_id);
      } catch (error) {
        // Silently fail
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') checkTelegramStatus(); };
    checkTelegramStatus();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkTelegramStatus);
    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', checkTelegramStatus);
    };
  }, [canShowTelegramCard]);

  return (
    <div className="space-y-6 relative z-10" data-testid="family-summary-page">
      <div data-net-exclude>
        <h1 className="ht-page-h1 flex items-center gap-2">
          <Home size={20} className="text-[#5f6672]" /> Riepilogo famiglia
        </h1>
        <p className="ht-page-sub">Risultati condivisi dei profili della tua famiglia. Sola lettura.</p>
      </div>

      {/* Collega Telegram - solo family_view senza Telegram collegato */}
      {canShowTelegramCard && !telegramLinked && freshUserId && (
        <div
          className="ht-panel p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          data-testid="family-telegram-link-card"
          data-net-exclude
        >
          <div className="flex items-center gap-3 min-w-0">
            <Send size={18} className="text-[#5f6672] flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="ht-panel-title text-sm">Ricevi i risultati su Telegram</h3>
              <p className="text-xs text-[#8b919c]">Tocca il pulsante e poi premi Avvia nel bot</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.open(`https://t.me/HedgingTrakerBot?start=${freshUserId}`, '_blank')}
            className="ht-btn ht-btn-primary w-full sm:w-auto justify-center flex-shrink-0"
            data-testid="family-telegram-link-btn"
          >
            <Send size={14} />
            Collega Telegram
          </button>
        </div>
      )}

      {loading && !summary && (
        <div className="space-y-4" data-testid="family-summary-loading">
          <div className="ht-card bg-[#101216] border border-[#23262d] rounded-xl p-5 space-y-4">
            <div className="skeleton-box h-5 w-44" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[0, 1].map((i) => (
                <div key={i} className="bg-[#0c0e11] rounded-lg p-4 border border-[#1e2127] space-y-3">
                  <div className="skeleton-box h-4 w-36" />
                  <div className="skeleton-box h-6 w-full" />
                  <div className="skeleton-box h-6 w-2/3" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {summary && (
        <>
          <AggregateSummaryCard
            summary={summary}
            title="Riepilogo Famiglia"
            onRefresh={load}
            loading={loading}
            animateNumbers
            testId="family-summary-card"
          />
          <div className="ht-panel p-4" data-testid="family-members-list" data-net-exclude>
            <p className="text-xs uppercase tracking-wider text-[#5f6672] flex items-center gap-1 mb-2">
              <Users size={12} /> Profili inclusi ({summary.all_users?.length || 0})
            </p>
            <div className="flex flex-wrap gap-2">
              {(summary.all_users || []).map(u => (
                <span key={u.id} className="ht-tag">{u.name}</span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
